import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiLogin, apiLogout, apiGetProfile, getToken as getLocalStorageToken } from '../api/api'; // Assurez-vous du chemin correct vers votre api.js
import { apiProfile } from '../api/api';

// Crée le contexte d'authentification
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // Informations de l'utilisateur connecté
    const [isAuthenticated, setIsAuthenticated] = useState(false); // État d'authentification
    const [loading, setLoading] = useState(true); // État de chargement initial

    // Fonction pour charger le profil utilisateur au démarrage ou après connexion/déconnexion
    const loadUserProfile = useCallback(async () => {
        setLoading(true);
        const token = getLocalStorageToken(); // Utilise la fonction getToken de votre api.js
        if (token) {
            try {
                const profile = await apiProfile(token);
                if (profile && !profile.error) {
                    setUser(profile);
                    setIsAuthenticated(true);
                } else {
                    console.error("Échec du chargement du profil:", profile.error);
                    apiLogout(); // Déconnecte si le profil ne peut pas être chargé
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error("Erreur réseau ou API lors du chargement du profil:", error);
                apiLogout();
                setUser(null);
                setIsAuthenticated(false);
            }
        } else {
            setUser(null);
            setIsAuthenticated(false);
        }
        setLoading(false);
    }, []);

    // Charge le profil utilisateur une fois au montage du composant
    useEffect(() => {
        loadUserProfile();
    }, [loadUserProfile]);

    // Fonction de connexion
    const login = useCallback(async (email, password) => {
        setLoading(true);
        const result = await apiLogin(email, password);
        setLoading(false);
        if (result && result.token) {
            // Le token est déjà stocké dans localStorage par apiLogin
            await loadUserProfile(); // Recharge le profil pour mettre à jour l'état
            return { success: true };
        } else {
            return { success: false, error: result.error || "Connexion échouée." };
        }
    }, [loadUserProfile]);

    // Fonction de déconnexion
    const logout = useCallback(() => {
        apiLogout(); // Supprime le token du localStorage
        setUser(null);
        setIsAuthenticated(false);
        // Optionnel: recharger la page ou rediriger ici si nécessaire
    }, []);

    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        // Si vous avez une fonction d'inscription, vous pouvez l'exposer ici aussi
        // register: apiRegister,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook personnalisé pour utiliser facilement le contexte d'authentification
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth doit être utilisé au sein d\'un AuthProvider');
    }
    return context;
};